import React from "react";
import { useNavigate } from "react-router-dom";
import { userLogout } from "../../common/service/CommonService";
import styles from "../../assets/scss/common/accessRestricted.module.scss";

interface AccessRestrictedProps {
  moduleName?: string;
}

const AccessRestricted: React.FC<AccessRestrictedProps> = () => {
  const navigate = useNavigate();

  const handleSignOut = () => {
    userLogout(navigate);
  };

  return (
    <div className={styles.root}>
      {/* ── Card Container ── */}
      <div className={styles.card}>
        
        {/* Centered Illustration */}
        <div className={styles.illustrationContainer}>
          <svg width="220" height="200" viewBox="0 0 220 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Soft background halo glow */}
            <circle cx="110" cy="100" r="90" fill="url(#illustration-halo)" />
            
            {/* Dashed orbit/swoosh path for dynamic Figma feel */}
            <path d="M45 135 C30 110 50 70 110 65 C170 60 195 105 175 135" stroke="#BFDBFE" stroke-width="2" stroke-linecap="round" stroke-dasharray="4 6" opacity="0.8" />
            
            {/* Floating decorative stars/sparkles */}
            <path d="M55 45 L57 49 L61 51 L57 53 L55 57 L53 53 L49 51 L53 49 Z" fill="#93C5FD" opacity="0.8" />
            <path d="M175 65 L176.5 68 L179.5 69.5 L176.5 71 L175 74 L173.5 71 L170.5 69.5 L173.5 68 Z" fill="#60A5FA" opacity="0.9" />
            <circle cx="48" cy="115" r="2.5" fill="#93C5FD" opacity="0.7" />
            <circle cx="178" cy="120" r="3" fill="#BFDBFE" opacity="0.8" />

            {/* MAIN FOLDER BACK LAYER */}
            <g filter="url(#folder-depth-shadow)">
              <path d="M50 72 C50 67.5817 53.5817 64 58 64 H86 C89.5 64 92 66.5 94.5 69.5 L104 80 H162 C166.418 80 170 83.5817 170 88 V136 C170 140.418 166.418 144 162 144 H58 C53.5817 144 50 140.418 50 136 Z" fill="url(#folder-back-gradient)" />
            </g>

            {/* INSERTED DOCUMENT SHEET */}
            <g filter="url(#document-shadow)">
              <rect x="74" y="46" width="72" height="84" rx="8" fill="#FFFFFF" />
              
              {/* User Avatar Group inside Document */}
              <g opacity="0.85">
                <rect x="94" y="56" width="32" height="20" rx="4" fill="#EFF6FF" />
                <circle cx="110" cy="63" r="3" fill="#3B82F6" />
                <path d="M104 70 C104 67.5 107 67 110 67 C113 67 116 67.5 116 70" stroke="#3B82F6" stroke-width="1.5" stroke-linecap="round" />
                <circle cx="104" cy="65" r="2.2" fill="#93C5FD" />
                <path d="M100 70.5 C100 68.5 102 68.2 104 68.2 C105.5 68.2 107 68.5 107 70.5" stroke="#93C5FD" stroke-width="1.2" stroke-linecap="round" />
                <circle cx="116" cy="65" r="2.2" fill="#93C5FD" />
                <path d="M113 70.5 C113 68.5 114.5 68.2 116 68.2 C118 68.2 120 68.5 120 70.5" stroke="#93C5FD" stroke-width="1.2" stroke-linecap="round" />
              </g>

              {/* Document Line Placeholders */}
              <rect x="86" y="84" width="48" height="3" rx="1.5" fill="#E2E8F0" />
              <rect x="86" y="92" width="36" height="3" rx="1.5" fill="#E2E8F0" />
              <rect x="86" y="100" width="42" height="3" rx="1.5" fill="#E2E8F0" />
            </g>

            {/* MAIN FOLDER FRONT LAYER */}
            <path d="M50 86 C50 81.5817 53.5817 78 58 78 H162 C166.418 78 170 81.5817 170 86 V136 C170 140.418 166.418 144 162 144 H58 C53.5817 144 50 140.418 50 136 Z" fill="url(#folder-front-gradient)" />
            
            {/* Subtle inner highlight border on front folder cover */}
            <path d="M51 87 C51 83.134 54.134 80 58 80 H162 C165.866 80 169 83.134 169 87" stroke="#93C5FD" stroke-width="1" stroke-linecap="round" opacity="0.4" />

            {/* SHIELD WITH LOCK */}
            <g filter="url(#shield-drop-shadow)">
              <path d="M120 102 C120 92 140 86 140 86 C140 86 160 92 160 102 C160 118 140 130 140 130 C140 130 120 118 120 102 Z" fill="url(#shield-bg-gradient)" />
              <path d="M123 103 C123 94.5 140 89.5 140 89.5 C140 89.5 157 94.5 157 103 C157 116.5 140 126.5 140 126.5 C140 126.5 123 116.5 123 103 Z" stroke="#FFFFFF" stroke-width="1.5" stroke-linecap="round" opacity="0.3" />
              
              {/* Lock Details */}
              <path d="M134 104 V101 C134 97.6863 136.686 95 140 95 C143.314 95 146 97.6863 146 101 V104" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" />
              <rect x="131" y="104" width="18" height="13" rx="3" fill="#FFFFFF" />
              <circle cx="140" cy="110.5" r="1.8" fill="#1E40AF" />
              <path d="M140 112 V114.5" stroke="#1E40AF" stroke-width="1.8" stroke-linecap="round" />
            </g>

            <defs>
              <radialGradient id="illustration-halo" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stop-color="#3B82F6" stop-opacity="0.18" />
                <stop offset="100%" stop-color="#3B82F6" stop-opacity="0" />
              </radialGradient>

              <linearGradient id="folder-back-gradient" x1="50" y1="64" x2="170" y2="144" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stop-color="#3B82F6" />
                <stop offset="100%" stop-color="#1E3A8A" />
              </linearGradient>

              <linearGradient id="folder-front-gradient" x1="50" y1="78" x2="170" y2="144" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stop-color="#60A5FA" />
                <stop offset="100%" stop-color="#2563EB" />
              </linearGradient>

              <linearGradient id="shield-bg-gradient" x1="120" y1="86" x2="160" y2="130" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stop-color="#2563EB" />
                <stop offset="100%" stop-color="#1D4ED8" />
              </linearGradient>

              <filter id="folder-depth-shadow" x="40" y="58" width="140" height="98" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                <feDropShadow dx="0" dy="8" stdDeviation="6" flood-color="#1E3A8A" flood-opacity="0.16" />
              </filter>

              <filter id="document-shadow" x="66" y="40" width="88" height="100" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                <feDropShadow dx="0" dy="6" stdDeviation="4" flood-color="#000000" flood-opacity="0.08" />
              </filter>

              <filter id="shield-drop-shadow" x="112" y="80" width="56" height="62" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                <feDropShadow dx="0" dy="6" stdDeviation="4" flood-color="#1E3A8A" flood-opacity="0.3" />
              </filter>
            </defs>
          </svg>
        </div>

        {/* Permission Required Pill */}
        <div className={styles.badgePill}>
          <i className="ri-lock-line" />
          <span>Permission Required</span>
        </div>

        {/* Title */}
        <h1 className={styles.title}>Access Restricted</h1>

        {/* Divider */}
        <div className={styles.divider} />

        {/* Description */}
        <p className={styles.description}>
          You don't have permission to access this page.
        </p>

        {/* Information box */}
        <div className={styles.infoCard}>
          <div className={styles.infoIconWrapper}>
            <i className="ri-information-line" />
          </div>
          <div className={styles.infoText}>
            This page is not available for your current role. Please reach out to your administrator to request permissions.
          </div>
        </div>

        {/* Action Button: Sign Out */}
        <button
          type="button"
          className={styles.primaryButton}
          onClick={handleSignOut}
        >
          <i className="ri-logout-box-r-line" />
          <span>Sign out</span>
        </button>

        {/* Bottom Footer Info */}
        <div className={styles.footer}>
          <i className="ri-shield-keyhole-line" />
          <span>Assigned permissions are managed through your system role.</span>
        </div>

      </div>
    </div>
  );
};

export default AccessRestricted;
